import { type Lesson } from "@core/grades/types";
import { formatDuration, formatNumber, money, URLS } from "@core/shared/utils";
import { BaseLink } from "@design-system/components/link";
import { platformFeaturesAtom } from "@framework/shared/atoms/platform-features.atom";
import { trans } from "@mongez/localization";
import {
  Clock3,
  Eye,
  GraduationCap,
  MessageCircle,
  Play,
  Star,
  UserRound,
} from "lucide-react";

type LessonDiscoveryCardProps = {
  lesson: Lesson;
  accentIndex: number;
};

const accentClasses = [
  "from-indigo-500 to-violet-500",
  "from-cyan-500 to-blue-500",
  "from-fuchsia-500 to-pink-500",
  "from-amber-500 to-orange-500",
  "from-emerald-500 to-teal-500",
];

export function LessonDiscoveryCard({
  lesson,
  accentIndex,
}: LessonDiscoveryCardProps) {
  const accentClass = accentClasses[accentIndex % accentClasses.length];
  const commentsAndRatingsEnabled =
    platformFeaturesAtom.useValue().commentsAndRatings === true;

  return (
    <article className="hover:shadow-primary-950/10 group relative flex h-full flex-col overflow-hidden rounded-[1.5rem] border border-slate-200/80 bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:border-primary-200 hover:shadow-xl dark:border-slate-700 dark:bg-slate-900 dark:hover:border-primary-700">
      <BaseLink
        aria-label={lesson.name}
        className="relative block aspect-[16/9] overflow-hidden bg-slate-100 dark:bg-slate-800"
        to={URLS.lessons.view(lesson)}
      >
        {lesson.image?.url ? (
          <img
            alt={lesson.name}
            className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
            loading="lazy"
            src={lesson.image.url}
          />
        ) : (
          <div
            className={`grid h-full place-items-center bg-gradient-to-br ${accentClass}`}
          >
            <GraduationCap className="size-14 text-white/80" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-transparent opacity-70" />
        <span className="absolute bottom-3 start-3 inline-flex items-center gap-1 rounded-full bg-slate-950/75 px-2.5 py-1 text-[11px] font-bold text-white backdrop-blur">
          <Play className="size-3 fill-current" />
          {trans("lessonCatalog.startLearning")}
        </span>
        {lesson.duration ? (
          <span
            className="absolute bottom-3 end-3 inline-flex items-center gap-1 rounded-full bg-white/90 px-2.5 py-1 text-[11px] font-black text-slate-800 backdrop-blur"
            dir="ltr"
          >
            <Clock3 className="size-3" />
            {formatDuration(lesson.duration)}
          </span>
        ) : null}
      </BaseLink>

      <div className="flex flex-1 flex-col p-4">
        <div className="mb-3 flex items-center justify-between gap-2">
          <span
            className={`inline-flex max-w-[70%] items-center gap-1 rounded-full bg-gradient-to-r px-2.5 py-1 text-[11px] font-bold text-white ${accentClass}`}
          >
            <GraduationCap className="size-3" />
            <span className="truncate">{lesson.grade?.name}</span>
          </span>
          <span className="shrink-0 text-[11px] font-bold text-primary-700 dark:text-primary-300">
            {lesson.subject?.name}
          </span>
        </div>

        <BaseLink to={URLS.lessons.view(lesson)}>
          <h3 className="line-clamp-2 min-h-12 text-base font-black leading-6 text-slate-950 transition group-hover:text-primary-700 dark:text-white dark:group-hover:text-primary-300">
            {lesson.name}
          </h3>
        </BaseLink>

        <div className="mt-3 flex items-center gap-2 border-b border-slate-100 pb-3 dark:border-slate-800">
          {lesson.instructor?.image?.url ? (
            <img
              alt={lesson.instructor.name}
              className="size-8 rounded-full border-2 border-white object-cover shadow-sm dark:border-slate-800"
              loading="lazy"
              src={lesson.instructor.image.url}
            />
          ) : (
            <span className="grid size-8 place-items-center rounded-full bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300">
              <UserRound className="size-4" />
            </span>
          )}
          <span className="min-w-0 truncate text-xs font-bold text-slate-600 dark:text-slate-300">
            {lesson.instructor?.name}
          </span>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-[11px] font-semibold text-slate-500 dark:text-slate-400">
          <span className="inline-flex items-center gap-1" dir="ltr">
            <Eye className="size-3.5" />
            {formatNumber(lesson.views || 0)}
          </span>
          {commentsAndRatingsEnabled ? (
            <>
              <span className="inline-flex items-center gap-1" dir="ltr">
                <MessageCircle className="size-3.5" />
                {formatNumber(lesson.comments || 0)}
              </span>
              <span className="inline-flex items-center gap-1" dir="ltr">
                <Star className="size-3.5 fill-amber-400 text-amber-400" />
                {lesson.rating || 0}
              </span>
            </>
          ) : null}
        </div>

        <div className="mt-auto flex items-end justify-between gap-3 pt-4">
          <span className="max-w-[65%] truncate text-xs font-bold text-slate-500 dark:text-slate-400">
            {lesson.category?.name || trans("grades.uncategorized")}
          </span>
          <strong
            className={`text-sm font-black ${
              lesson.isFree
                ? "text-emerald-600 dark:text-emerald-400"
                : "text-slate-950 dark:text-white"
            }`}
          >
            {lesson.isFree ? trans("free") : money(lesson.cost)}
          </strong>
        </div>
      </div>
    </article>
  );
}
